import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Order() {
  const [searchParams] = useSearchParams();
  const initialItem = searchParams.get("item");
  const initialLocation = searchParams.get("location") || "rochester";
  const initialType = searchParams.get("type") || "pickup";

  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    email: "",
    location: initialLocation,
    order_type: initialType,
    address: "",
    notes: "",
  });
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => {
      setMenu(r.data);
      if (initialItem) {
        const it = r.data.find((m) => m.slug === initialItem);
        if (it) setCart([{ ...it, quantity: 1 }]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = (item) => {
    setCart((c) => {
      const existing = c.find((x) => x.slug === item.slug);
      if (existing) {
        return c.map((x) =>
          x.slug === item.slug ? { ...x, quantity: x.quantity + 1 } : x
        );
      }
      return [...c, { ...item, quantity: 1 }];
    });
  };

  const updateQty = (slug, delta) => {
    setCart((c) =>
      c
        .map((x) => (x.slug === slug ? { ...x, quantity: x.quantity + delta } : x))
        .filter((x) => x.quantity > 0)
    );
  };

  const removeItem = (slug) => setCart((c) => c.filter((x) => x.slug !== slug));

  const subtotal = cart.reduce((s, x) => s + x.price_from * x.quantity, 0);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    if (!form.customer_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    if (form.order_type === "delivery" && !form.address) {
      toast.error("Delivery address is required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: cart.map((c) => ({ slug: c.slug, quantity: c.quantity })),
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setConfirmed(data);
      setCart([]);
      toast.success("Order placed!");
    } catch (err) {
      toast.error("Order failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div data-testid="order-confirmed-page">
        <section className="sd-page-hero">
          <h1>Order Confirmed!</h1>
        </section>
        <section className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-6 bg-[#f6f1e8] p-8 rounded">
            <h2 className="text-2xl text-[#cd0202] font-display mb-2">Thanks, {confirmed.customer_name}!</h2>
            <p className="text-gray-700 mb-4">
              Your order <strong>#{confirmed.id.slice(0, 8)}</strong> has been received.
              We'll have it ready at the{" "}
              <strong>{confirmed.location === "rochester" ? "Rochester Rd." : "Locust St."}</strong>{" "}
              shop. (Placeholder — no real payment processed.)
            </p>
            <div className="bg-white p-4 rounded mb-4">
              {confirmed.items.map((it) => (
                <div key={it.slug} className="flex justify-between py-2 border-b last:border-0">
                  <span>{it.quantity} × {it.name}</span>
                  <span>${it.line_total.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 text-gray-700">
                <span>Subtotal</span>
                <span>${confirmed.subtotal.toFixed(2)}</span>
              </div>
              {confirmed.discount > 0 && (
                <div className="flex justify-between text-[#27c004]">
                  <span>First-order discount (10%)</span>
                  <span>−${confirmed.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 font-display text-xl text-[#cd0202]">
                <span>Total</span>
                <span>${confirmed.total.toFixed(2)}</span>
              </div>
            </div>
            <Link to="/" className="sd-btn-red" data-testid="back-home">Back to Home</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="order-page">
      <section className="sd-page-hero">
        <h1>Online Ordering</h1>
        <p className="text-white/95 mt-3">
          First-time customer? <strong>10% off automatically</strong> at checkout.
        </p>
      </section>

      <section className="py-12 bg-[#f6f1e8]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {/* Menu list */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl text-[#cd0202] font-display mb-4">Choose your items</h2>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="order-menu-list">
              {menu.map((m) => (
                <div
                  key={m.slug}
                  className="sd-order-card flex gap-3"
                  data-testid={`order-item-${m.slug}`}
                >
                  <img src={m.image} alt={m.name} className="w-24 h-24 object-cover rounded" />
                  <div className="flex-1">
                    <h3 className="text-[#cd0202] font-display text-lg leading-tight">{m.name}</h3>
                    <p className="text-gray-600 text-xs line-clamp-2 mt-1">{m.description}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="font-display text-[#cd0202]">{m.price_label}</span>
                      <button
                        onClick={() => addItem(m)}
                        className="bg-[#cd0202] text-white px-3 py-1 rounded-full text-sm font-display flex items-center gap-1 hover:bg-[#ff1111]"
                        data-testid={`add-to-cart-${m.slug}`}
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cart + checkout */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="sd-order-card mb-4">
                <h2 className="text-xl text-[#cd0202] font-display mb-3">Your Order</h2>
                {cart.length === 0 && (
                  <p className="text-gray-500 text-sm" data-testid="cart-empty">Cart is empty.</p>
                )}
                <div className="space-y-2" data-testid="cart-items">
                  {cart.map((c) => (
                    <div key={c.slug} className="flex items-center gap-2 text-sm" data-testid={`cart-item-${c.slug}`}>
                      <div className="flex-1">
                        <div className="font-display">{c.name}</div>
                        <div className="text-gray-500 text-xs">${c.price_from.toFixed(2)} each</div>
                      </div>
                      <button onClick={() => updateQty(c.slug, -1)} className="p-1 border rounded" data-testid={`cart-dec-${c.slug}`}><Minus size={12} /></button>
                      <span data-testid={`cart-qty-${c.slug}`}>{c.quantity}</span>
                      <button onClick={() => updateQty(c.slug, 1)} className="p-1 border rounded" data-testid={`cart-inc-${c.slug}`}><Plus size={12} /></button>
                      <button onClick={() => removeItem(c.slug)} className="p-1 text-red-600" data-testid={`cart-rm-${c.slug}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                {cart.length > 0 && (
                  <div className="border-t mt-3 pt-3 flex justify-between font-display text-[#cd0202]">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <form onSubmit={placeOrder} className="sd-order-card space-y-3" data-testid="checkout-form">
                <h2 className="text-xl text-[#cd0202] font-display">Details</h2>

                <div className="grid grid-cols-2 gap-2">
                  <label className="text-sm">
                    Location
                    <select
                      value={form.location}
                      onChange={upd("location")}
                      className="w-full mt-1 border px-2 py-2 rounded"
                      data-testid="checkout-location"
                    >
                      <option value="rochester">Rochester Rd.</option>
                      <option value="locust">Locust St.</option>
                    </select>
                  </label>
                  <label className="text-sm">
                    Type
                    <select
                      value={form.order_type}
                      onChange={upd("order_type")}
                      className="w-full mt-1 border px-2 py-2 rounded"
                      data-testid="checkout-type"
                    >
                      <option value="pickup">Pick-up</option>
                      <option value="delivery">Delivery</option>
                    </select>
                  </label>
                </div>

                <input
                  type="text"
                  placeholder="Full name *"
                  value={form.customer_name}
                  onChange={upd("customer_name")}
                  className="w-full border px-3 py-2 rounded"
                  data-testid="checkout-name"
                />
                <input
                  type="tel"
                  placeholder="Phone *"
                  value={form.phone}
                  onChange={upd("phone")}
                  className="w-full border px-3 py-2 rounded"
                  data-testid="checkout-phone"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={upd("email")}
                  className="w-full border px-3 py-2 rounded"
                  data-testid="checkout-email"
                />
                {form.order_type === "delivery" && (
                  <input
                    type="text"
                    placeholder="Delivery address *"
                    value={form.address}
                    onChange={upd("address")}
                    className="w-full border px-3 py-2 rounded"
                    data-testid="checkout-address"
                  />
                )}
                <textarea
                  placeholder="Notes (optional)"
                  value={form.notes}
                  onChange={upd("notes")}
                  rows={2}
                  className="w-full border px-3 py-2 rounded"
                  data-testid="checkout-notes"
                />
                <button
                  type="submit"
                  className="sd-btn-red w-full"
                  disabled={submitting}
                  data-testid="checkout-submit"
                >
                  {submitting ? "Placing order..." : "Place Order (Placeholder)"}
                </button>
                <p className="text-xs text-gray-500">
                  This is a placeholder flow — no real payment is processed.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
